import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import classNames from 'classnames';
import type { FormField, FormFieldValue, FormValues } from './types';
import { buildValidationSchema } from './schema';

type EditingContextValue = {
  editingField: string | null;
  editValue: FormFieldValue;
  startEdit: (name: string) => void;
  saveEdit: () => void;
  cancelEdit: () => void;
  setEditValue: (v: FormFieldValue) => void;
  submitting: boolean;
};

const EditingContext = createContext<EditingContextValue | null>(null);

export function useEditingContext() {
  const ctx = useContext(EditingContext);
  if (!ctx)
    throw new Error('useEditingContext must be used within FormProvider');
  return ctx;
}

export { useFormikContext };

type FormProviderProps = {
  fields: FormField[];
  initialValues: FormValues;
  submitting: boolean;
  onSubmit: (values: FormValues) => void | Promise<void>;
  containerClassName?: string;
  formClassName?: string;
  children: React.ReactNode;
};

export function FormProvider({
  fields,
  initialValues,
  submitting,
  onSubmit,
  containerClassName,
  formClassName,
  children,
}: FormProviderProps) {
  const validationSchema = useMemo(
    () => buildValidationSchema(fields),
    [fields]
  );

  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<FormFieldValue>(null);

  const startEdit = useCallback((name: string) => {
    setEditingField(name);
  }, []);

  const saveEdit = useCallback(() => {
    setEditingField(null);
    setEditValue(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingField(null);
    setEditValue(null);
  }, []);

  const editingCtx = useMemo<EditingContextValue>(
    () => ({
      editingField,
      editValue,
      startEdit,
      saveEdit,
      cancelEdit,
      setEditValue,
      submitting,
    }),
    [editingField, editValue, startEdit, saveEdit, cancelEdit, submitting]
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      <EditingContext.Provider value={editingCtx}>
        <div className="flex flex-col h-full overflow-auto">
          <div
            className={classNames(
              'w-full max-w-5xl mx-auto px-4 py-8 sm:px-6 lg:px-8',
              containerClassName
            )}
          >
            <Form className={classNames('flex flex-col gap-8', formClassName)}>
              {children}
            </Form>
          </div>
        </div>
      </EditingContext.Provider>
    </Formik>
  );
}
